import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Camera, ListOrdered, MoveVertical, Plus, Ruler, UserRound, Weight } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { BotonEnlace } from '@/components/Button';
import { Card, EstadoVacio, Seccion } from '@/components/Card';
import { Fila, Filas } from '@/components/Fila';
import { GraficaSerie } from '@/components/GraficaSerie';
import { leerSettings } from '@/db/schema';
import { leerBasculas } from '@/db/basculas';
import { contarPesajesIlegibles, leerPesajes } from '@/db/pesajes';
import { leerMedidas } from '@/db/medidas';
import { leerFotos } from '@/db/fotos';
import { leerPlanes } from '@/db/planes';
import type { Scale, Settings, WeighIn } from '@/db/types';
import { franjasDeEtapas } from '@/domain/etapas';
import { porDia, textoDelEspacio } from '@/domain/fotos';
import { fechaCorta } from '@/domain/hoy';
import { formatearImc, imcDeHoy } from '@/domain/imc';
import { NOMBRE_MEDIDA, formatearMedida } from '@/domain/medidas';
import { formatearNumero } from '@/domain/numbers';
import { formatearHora } from '@/domain/time';
import { desdeKg, formatearLongitud, formatearPeso } from '@/domain/units';
import { formatearCambio, pesosPorDia, resumenDePeso, serieDePeso, type ResumenDePeso } from '@/domain/tendencia';
import { useFechaHoy } from '@/lib/useFechaHoy';

/**
 * Pesajes guardados que no se pueden leer (fecha, hora o peso dañados, por
 * ejemplo de un respaldo editado a mano): no se muestran ni cuentan y siguen
 * guardados tal cual; se dice cuántos (revisión de la 0.4.0).
 */
export function AvisoIlegibles({ cantidad }: { cantidad: number }) {
  if (cantidad === 0) return null;
  const uno = cantidad === 1;
  return (
    <div className="mb-6">
      <Banner tono="aviso" titulo={uno ? '1 pesaje no se puede leer.' : `${cantidad} pesajes no se pueden leer.`}>
        {`${uno ? 'Tiene' : 'Tienen'} la fecha, la hora o el peso dañados (pudo llegar así en un respaldo): no se ${
          uno ? 'muestra ni cuenta' : 'muestran ni cuentan'
        } en tu tendencia, y ${uno ? 'sigue guardado' : 'siguen guardados'} tal cual.`}
      </Banner>
    </div>
  );
}

/** "Mar, 27 oct · 7:05 a. m. · Casa" */
export function cuandoYDonde(p: WeighIn, basculas: readonly Scale[]): string {
  const bascula = p.scaleId === null ? null : (basculas.find((b) => b.id === p.scaleId)?.nombre ?? null);
  let hora: string | null = null;
  try {
    hora = formatearHora(p.hora);
  } catch {
    hora = null;
  }
  return [fechaCorta(p.fecha), hora, bascula].filter(Boolean).join(' · ');
}

/**
 * Cuerpo (Fase 3; Req. 14 a 16): la tendencia de 7 días con su cambio, el
 * último pesaje, el IMC y la gráfica con las etapas del plan. Todo sale de
 * los pesajes: nada se guarda calculado.
 */
export function BodyPage() {
  const hoy = useFechaHoy();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const pesajes = useLiveQuery(() => leerPesajes(), []);
  const ilegibles = useLiveQuery(() => contarPesajesIlegibles(), []);
  const basculas = useLiveQuery(() => leerBasculas(), []);
  const planes = useLiveQuery(() => leerPlanes(), []);
  const medidas = useLiveQuery(() => leerMedidas(), []);
  const fotos = useLiveQuery(() => leerFotos(), []);
  if (
    ajustes === undefined ||
    pesajes === undefined ||
    ilegibles === undefined ||
    basculas === undefined ||
    planes === undefined ||
    medidas === undefined ||
    fotos === undefined
  ) {
    return null;
  }
  // La más reciente (0.4.1)
  const ultimaMedida = medidas[0];
  // La foto del día más reciente (0.14.0)
  const ultimaFoto = porDia(fotos)[0]?.fotos.at(-1);

  const resumen = resumenDePeso(pesajes, ajustes.basculaTendenciaId, hoy);
  const deTendencia = basculas.find((b) => b.id === ajustes.basculaTendenciaId) ?? null;
  const activas = basculas.filter((b) => !b.archivada);

  return (
    <div>
      <PageHeader compacto titulo="Tu progreso" descripcion="Cada cambio forma parte de tu proceso." />

      <AvisoIlegibles cantidad={ilegibles} />

      {resumen.total === 0 ? (
        <EstadoVacio
          icono={Weight}
          titulo="Tu punto de partida"
          descripcion="Aquí verás tu peso, su tendencia de 7 días y tu IMC con el tiempo. La tendencia suaviza las subidas y bajadas de cada día."
        >
          <BotonEnlace to="/cuerpo/pesaje/nuevo" icono={Plus}>
            Registrar mi peso
          </BotonEnlace>
        </EstadoVacio>
      ) : (
        <PanelDePeso resumen={resumen} ajustes={ajustes} basculas={basculas} deTendencia={deTendencia} />
      )}

      {resumen.total > 0 && (
        <div className="mt-5">
          <Seccion titulo="Tu peso en el tiempo" descripcion="Cada punto es el peso del día (el primero de ese día) y la línea, su tendencia de 7 días.">
            <GraficaDePeso pesajes={pesajes} ajustes={ajustes} hoy={hoy} franjas={franjasDeEtapas(planes.etapas)} />
          </Seccion>
        </div>
      )}

      {/* Lo que se consulta, en dos tarjetas; lo que se configura, abajo (0.11.0) */}
      <Filas tarjetas rejilla className="mt-5">
        <Fila
          tarjeta
          icono={ListOrdered}
          titulo="Pesajes"
          detalle={
            resumen.ultimo === null
              ? 'Todavía no hay pesajes'
              : `${resumen.total === 1 ? '1 pesaje' : `${resumen.total} pesajes`} · el último, ${fechaCorta(resumen.ultimo.fecha).toLocaleLowerCase('es-MX')}`
          }
          a="/cuerpo/pesajes"
        />
        <Fila
          tarjeta
          icono={Ruler}
          titulo="Medidas"
          detalle={
            ultimaMedida === undefined
              ? 'Registrar y consultar'
              : `${NOMBRE_MEDIDA[ultimaMedida.tipo]}: ${formatearMedida(ultimaMedida.valor, ultimaMedida.tipo, ajustes.unidadLongitud)}`
          }
          a="/cuerpo/medidas"
        />
        <Fila
          tarjeta
          icono={Camera}
          titulo="Fotos"
          detalle={
            fotos.length === 0
              ? 'El espejo cambia antes que la báscula'
              : `${textoDelEspacio(fotos)} · la última, ${fechaCorta(ultimaFoto?.fecha ?? hoy).toLocaleLowerCase('es-MX')}`
          }
          a="/cuerpo/fotos"
        />
      </Filas>

      <section aria-labelledby="titulo-configuracion-cuerpo" className="mt-5">
        <h2 id="titulo-configuracion-cuerpo" className="mb-2 text-[17px] tracking-[-0.25px] text-texto">
          Configuración
        </h2>
        <Filas tarjetas>
          <Fila
            tarjeta
            icono={Weight}
            titulo="Básculas"
            detalle={
              activas.length === 0
                ? 'Agrega las tuyas con su unidad'
                : deTendencia === null
                  ? `${activas.map((b) => `${b.nombre} (${b.unidad})`).join(', ')} · cuentan todas`
                  : `${activas.map((b) => `${b.nombre} (${b.unidad})`).join(', ')} · cuenta ${deTendencia.nombre}`
            }
            a="/cuerpo/basculas"
          />
          <Fila
            tarjeta
            icono={MoveVertical}
            titulo="Estatura"
            {...(ajustes.estaturaCm === null
              ? { detalle: 'Sin estatura: hace falta para el IMC' }
              : {
                  detalle: 'Para tu IMC',
                  extra: (
                    <span className="shrink-0 text-[13px] tabular-nums text-texto-suave">
                      {formatearLongitud(ajustes.estaturaCm, ajustes.unidadLongitud)}
                    </span>
                  ),
                })}
            a="/cuerpo/estatura"
          />
          <Fila tarjeta icono={UserRound} titulo="Mis datos" detalle="Revisar perfil y unidades" a="/mas/perfil" />
        </Filas>
      </section>
    </div>
  );
}

function PanelDePeso({
  resumen,
  ajustes,
  basculas,
  deTendencia,
}: {
  resumen: ResumenDePeso<WeighIn>;
  ajustes: Settings;
  basculas: readonly Scale[];
  deTendencia: Scale | null;
}) {
  const unidad = ajustes.unidadPeso;
  const imc = imcDeHoy(resumen, ajustes.estaturaCm);
  const ultimo = resumen.ultimo;

  return (
    <Card>
      <span className="antetitulo">Tendencia de 7 días</span>
      {resumen.tendenciaKg === null ? (
        <>
          <p className="mt-2 text-[17px] leading-snug text-texto">Sin pesajes en los últimos 7 días.</p>
          <p className="mt-1 text-[13px] leading-relaxed text-texto-suave">
            {resumen.ultimoDiaConPeso === null
              ? deTendencia === null
                ? 'La tendencia sale de los pesajes de cada día.'
                : `Cuenta solo ${deTendencia.nombre} y todavía no tiene pesajes.`
              : `Tu último peso del día que cuenta fue el ${fechaCorta(resumen.ultimoDiaConPeso).toLocaleLowerCase('es-MX')}.`}
          </p>
        </>
      ) : (
        <>
          <p className="mt-2 text-[34px] leading-none tracking-[-1px] tabular-nums text-texto">
            {formatearPeso(resumen.tendenciaKg, unidad)}
          </p>
          <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
            {resumen.cambioSemanalKg === null
              ? 'Cambio semanal: faltan datos. Aparece cuando hay pesajes también en la semana anterior.'
              : `Cambio semanal: ${formatearCambio(resumen.cambioSemanalKg, unidad)} contra hace 7 días`}
            {deTendencia !== null && ` · cuenta solo ${deTendencia.nombre}`}
          </p>
        </>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-borde pt-4">
        <div className="min-w-0">
          <dt className="text-[12px] text-texto-suave">Último peso</dt>
          {ultimo === null ? (
            <dd className="mt-1 text-[13px] text-texto">Sin pesajes</dd>
          ) : (
            <dd className="mt-1">
              <span className="block text-[17px] tabular-nums text-texto">{formatearPeso(ultimo.pesoKg, unidad)}</span>
              <span className="block text-[12px] text-texto-suave">{cuandoYDonde(ultimo, basculas)}</span>
            </dd>
          )}
        </div>
        <div className="min-w-0">
          <dt className="text-[12px] text-texto-suave">IMC</dt>
          {imc.tipo === 'listo' ? (
            <dd className="mt-1">
              <span className="block text-[17px] tabular-nums text-texto">{formatearImc(imc.valor)}</span>
              <span className="block text-[12px] text-texto-suave">
                {imc.base === 'tendencia' ? 'Con tu tendencia' : 'Con tu último pesaje'} y{' '}
                {formatearLongitud(ajustes.estaturaCm ?? 0, ajustes.unidadLongitud)}
              </span>
            </dd>
          ) : (
            <dd className="mt-1">
              <span className="block text-[13px] text-texto">Falta tu estatura</span>
              <Link to="/cuerpo/estatura" className="inline-flex min-h-11 items-center text-[13px] font-medium text-marca">
                Agregar estatura
              </Link>
            </dd>
          )}
        </div>
      </dl>

      <div className="mt-4">
        <BotonEnlace to="/cuerpo/pesaje/nuevo" icono={Plus}>
          Registrar peso
        </BotonEnlace>
      </div>
    </Card>
  );
}

/** La gráfica de peso en la unidad preferida, con las etapas del plan (Req. 16.4 a 16.6). */
function GraficaDePeso({
  pesajes,
  ajustes,
  hoy,
  franjas,
}: {
  pesajes: readonly WeighIn[];
  ajustes: Settings;
  hoy: string;
  franjas: ReturnType<typeof franjasDeEtapas>;
}) {
  const unidad = ajustes.unidadPeso;
  const dias = [...pesosPorDia(pesajes, ajustes.basculaTendenciaId).keys()].sort();
  const primero = dias[0] ?? hoy;
  const serie = serieDePeso(pesajes, ajustes.basculaTendenciaId, primero < hoy ? primero : hoy, hoy);
  // La gráfica recibe los valores ya en la unidad preferida (Req. 5.7): el eje
  // queda en esa unidad y aquí solo se presentan, sin volver a convertir
  const enUnidad = (p: { fecha: string; valor: number }) => ({ fecha: p.fecha, valor: desdeKg(p.valor, unidad) });
  const formatear = (v: number) => `${formatearNumero(v, { decimales: 1 })} ${unidad}`;
  const cambio = (v: number) => {
    const texto = formatearNumero(v, { decimales: 1 });
    return v > 0 && texto !== '0.0' ? `+${texto} ${unidad}` : `${texto} ${unidad}`;
  };

  return (
    <GraficaSerie
      etiqueta="Periodo de la gráfica de peso"
      puntos={serie.pesos.map(enUnidad)}
      nombrePuntos="Peso del día"
      linea={serie.tendencia.map(enUnidad)}
      nombreLinea="Tendencia de 7 días"
      franjas={franjas}
      formatear={formatear}
      formatearCambio={cambio}
      textoCantidad={(n) => (n === 1 ? '1 día con peso' : `${n} días con peso`)}
      unidad={unidad}
      vacio="Sin pesajes en este periodo. Elige uno más largo para ver los anteriores."
      hoy={hoy}
    />
  );
}
